/**
 * TanStack Query hooks for user preferences with optimistic updates
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import {
	type UserPreferences,
	createPreferenceUpdate,
	createViewPreferenceUpdate,
	getUserPreferences,
} from '@/utils/userPreferences'
import { toast } from 'sonner'

// Query keys
export const userPreferencesKeys = {
	all: ['userPreferences'] as const,
	member: (memberId: string) => [...userPreferencesKeys.all, memberId] as const,
}

/**
 * Query hook to get user preferences
 */
export function useUserPreferencesQuery() {
	const { member } = useStytchMember()

	return useQuery({
		queryKey: userPreferencesKeys.member(member?.member_id || 'no-member'),
		queryFn: () => getUserPreferences(member),
		enabled: !!member,
		staleTime: 1000 * 60 * 5, // 5 minutes
	})
}

/**
 * Mutation to update a user preference with optimistic updates
 */
export function useUpdatePreferenceMutation() {
	const { member } = useStytchMember()
	const stytch = useStytchB2BClient()
	const queryClient = useQueryClient()

	return useMutation<
		{ key: keyof UserPreferences; value: UserPreferences[keyof UserPreferences] },
		Error,
		{ key: keyof UserPreferences; value: UserPreferences[keyof UserPreferences] },
		{ previousPreferences: UserPreferences | undefined }
	>({
		mutationFn: async ({
			key,
			value,
		}) => {
			if (!member?.member_id) {
				throw new Error('No member ID')
			}

			const update = createPreferenceUpdate(member, key, value)
			await stytch.self.update(update)

			return { key, value }
		},
		onMutate: async ({ key, value }) => {
			if (!member?.member_id) return { previousPreferences: undefined }

			const queryKey = userPreferencesKeys.member(member.member_id)

			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous value
			const previousPreferences = queryClient.getQueryData<UserPreferences>(queryKey)

			// Optimistically update
			queryClient.setQueryData<UserPreferences>(queryKey, (old) => {
				if (!old) return getUserPreferences(member)
				return {
					...old,
					[key]: value,
				}
			})

			return { previousPreferences }
		},
		onError: (error, _variables, context) => {
			// Rollback on error
			if (member?.member_id && context?.previousPreferences) {
				queryClient.setQueryData(
					userPreferencesKeys.member(member.member_id),
					context.previousPreferences
				)
			}
			console.error('Failed to update user preference:', error)
			toast.error('Failed to save preference')
		},
		onSuccess: ({ key, value }) => {
			// Don't invalidate - trust the optimistic update
			// Stytch member object may not be immediately updated after the API call
			if (member?.member_id) {
				const queryKey = userPreferencesKeys.member(member.member_id)
				// Confirm the cache has the correct value
				queryClient.setQueryData<UserPreferences>(queryKey, (old) => {
					if (!old) return getUserPreferences(member)
					return {
						...old,
						[key]: value,
					}
				})
			}
		},
	})
}

/**
 * Mutation to update a view preference with optimistic updates
 */
export function useUpdateViewPreferenceMutation() {
	const { member } = useStytchMember()
	const stytch = useStytchB2BClient()
	const queryClient = useQueryClient()

	return useMutation<
		{ entity: keyof UserPreferences['viewPreferences']; view: 'table' | 'cards' },
		Error,
		{ entity: keyof UserPreferences['viewPreferences']; view: 'table' | 'cards' },
		{ previousPreferences: UserPreferences | undefined }
	>({
		mutationFn: async ({
			entity,
			view,
		}) => {
			if (!member?.member_id) {
				throw new Error('No member ID')
			}

			const update = createViewPreferenceUpdate(member, entity, view)
			await stytch.self.update(update)

			return { entity, view }
		},
		onMutate: async ({ entity, view }) => {
			if (!member?.member_id) return { previousPreferences: undefined }

			const queryKey = userPreferencesKeys.member(member.member_id)

			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous value
			const previousPreferences = queryClient.getQueryData<UserPreferences>(queryKey)

			// Optimistically update
			queryClient.setQueryData<UserPreferences>(queryKey, (old) => {
				const current = old || getUserPreferences(member)
				return {
					...current,
					viewPreferences: {
						...current.viewPreferences,
						[entity]: view,
					},
				}
			})

			return { previousPreferences }
		},
		onError: (error, _variables, context) => {
			// Rollback on error
			if (member?.member_id && context?.previousPreferences) {
				queryClient.setQueryData(
					userPreferencesKeys.member(member.member_id),
					context.previousPreferences
				)
			}
			console.error('Failed to update view preference:', error)
			toast.error('Failed to save view preference')
		},
		onSuccess: ({ entity, view }) => {
			// Don't invalidate - trust the optimistic update
			// Stytch member object may not be immediately updated after the API call
			if (member?.member_id) {
				const queryKey = userPreferencesKeys.member(member.member_id)
				// Confirm the cache has the correct value
				queryClient.setQueryData<UserPreferences>(queryKey, (old) => {
					const current = old || getUserPreferences(member)
					return {
						...current,
						viewPreferences: {
							...current.viewPreferences,
							[entity]: view,
						},
					}
				})
			}
		},
	})
}
